import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { formatDateRange, formatDate, parseISODateString } from "@/lib/formatters";

// Format currency without RM prefix, with commas and 2 decimals; negatives in parentheses
const fmt = (value) => {
  const num = parseFloat(value) || 0;
  const abs = Math.abs(num).toLocaleString("en-MY", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return num < 0 ? `(${abs})` : abs;
};

const fmtPct = (value) => {
  const num = parseFloat(value) || 0;
  const abs = Math.abs(num).toFixed(2);
  return num < 0 ? `(${abs}%)` : `${abs}%`;
};

// Verify totals reconcile
export const reconcileEvent = (event) => {
  const products = event.products || [];
  const staff = event.staffWages || [];

  const calcRevenue = products.reduce((s, p) => s + (p.totalSalesAmount || 0), 0);
  const calcCogs = products.reduce((s, p) => s + (p.productCost || 0), 0);
  const calcStaff = staff.reduce((s, w) => s + (w.wageAmount || 0), 0);
  const calcOpex =
    (event.rentalCost || 0) + calcStaff + (event.transportCost || 0) + (event.otherCost || 0);

  // With sales reconciliation, Total Revenue = totalSalesCollected (fallback to totalSales)
  const revenue = event.totalSalesCollected != null ? event.totalSalesCollected : event.totalSales;
  const totalCogs = event.totalCostOfGoodsSold != null ? event.totalCostOfGoodsSold : event.totalProductCost;
  const calcNet = revenue - totalCogs - calcOpex;

  const tol = 0.02;
  const eq = (a, b) => Math.abs((a || 0) - (b || 0)) < tol;

  return (
    eq(calcRevenue, event.totalRecordedProductSales != null ? event.totalRecordedProductSales : event.totalSales) &&
    eq(calcCogs, event.totalProductCost) &&
    eq(calcStaff, event.totalStaffWages) &&
    eq(calcNet, event.netProfit)
  );
};

// Sanitize filename
const sanitizeFilename = (str) =>
  str
    .replace(/[<>:"/\\|?*]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

export const generateEventPDF = (event) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 40;
  const contentWidth = pageWidth - 2 * margin;

  // Group products by category
  const productsByCategory = (event.products || []).reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const categoryTotals = Object.entries(productsByCategory).map(([category, prods]) => {
    const revenue = prods.reduce((s, p) => s + (p.totalSalesAmount || 0), 0);
    const cogs = prods.reduce((s, p) => s + (p.productCost || 0), 0);
    return { category, revenue, cogs, gross: revenue - cogs, products: prods };
  });

  const totalRecordedProductCost = event.totalProductCost || 0;
  const estimatedUnrecordedCogs = event.estimatedUnrecordedCogs || 0;
  const totalRevenue = event.totalSalesCollected != null ? event.totalSalesCollected : (event.totalSales || 0);
  const totalRecordedProductSales = event.totalRecordedProductSales != null ? event.totalRecordedProductSales : (event.totalSales || 0);
  const totalCogs = event.totalCostOfGoodsSold != null ? event.totalCostOfGoodsSold : totalRecordedProductCost;
  const unrecordedSalesAmount = event.unrecordedSalesAmount != null ? event.unrecordedSalesAmount : (totalRevenue - totalRecordedProductSales);
  const grossProfit = totalRevenue - totalCogs;
  const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
  const totalStaffWages = event.totalStaffWages || 0;
  const totalOpex =
    (event.rentalCost || 0) + totalStaffWages + (event.transportCost || 0) + (event.otherCost || 0);
  const netProfit = grossProfit - totalOpex;
  const netMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

  const reportGeneratedDate = formatDate(new Date().toISOString().split("T")[0]);

  // ===== FIRST PAGE HEADER =====
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("GAO CHILL", pageWidth / 2, margin + 6, { align: "center" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text("Herbal Beverage Bazaar Business", pageWidth / 2, margin + 22, { align: "center" });

  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.text("MANAGEMENT PROFIT & LOSS STATEMENT", pageWidth / 2, margin + 48, { align: "center" });

  // Horizontal rule under title
  doc.setLineWidth(0.8);
  doc.line(margin, margin + 58, pageWidth - margin, margin + 58);

  // Event info block
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  let infoY = margin + 78;
  const infoLineHeight = 14;

  const infoLines = [
    ["Event Name:", event.eventName],
    ["Event Period:", `${formatDate(event.startDate)} to ${formatDate(event.endDate)}`],
    ["Total Event Days:", `${event.totalEventDays}`],
    ["Currency:", "Malaysian Ringgit (RM)"],
    ["Report Generated:", reportGeneratedDate],
  ];

  infoLines.forEach(([label, value]) => {
    doc.setFont("helvetica", "bold");
    doc.text(label, margin, infoY);
    doc.setFont("helvetica", "normal");
    doc.text(String(value), margin + 110, infoY);
    infoY += infoLineHeight;
  });

  // ===== MAIN P&L TABLE =====
  const startY = infoY + 12;

  const plBody = [];

  // Revenue section
  plBody.push([{ content: "REVENUE", styles: { fontStyle: "bold" } }, ""]);
  plBody.push(["    Sales Revenue", fmt(totalRevenue)]);
  plBody.push([
    { content: "TOTAL REVENUE", styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
    { content: fmt(totalRevenue), styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
  ]);
  plBody.push(["", ""]);

  // COGS section — simplified: Recorded Product Cost + Estimated COGS
  plBody.push([{ content: "LESS: COST OF GOODS SOLD", styles: { fontStyle: "bold" } }, ""]);
  plBody.push(["    Recorded Product Cost", fmt(totalRecordedProductCost)]);
  plBody.push(["    Estimated COGS — Unrecorded Cup Sales", fmt(estimatedUnrecordedCogs)]);
  plBody.push([
    { content: "TOTAL COST OF GOODS SOLD", styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
    { content: fmt(totalCogs), styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
  ]);
  plBody.push(["", ""]);

  plBody.push([
    { content: "GROSS PROFIT", styles: { fontStyle: "bold" } },
    { content: fmt(grossProfit), styles: { fontStyle: "bold" } },
  ]);
  plBody.push(["    Gross Profit Margin", fmtPct(grossMargin)]);
  plBody.push(["", ""]);

  // Operating Expenses
  plBody.push([{ content: "LESS: OPERATING EXPENSES", styles: { fontStyle: "bold" } }, ""]);
  plBody.push(["    Event Rental", fmt(event.rentalCost || 0)]);
  plBody.push(["    Staff Wages", fmt(totalStaffWages)]);
  plBody.push(["    Transport Expense", fmt(event.transportCost || 0)]);
  plBody.push(["    Other Event Expenses", fmt(event.otherCost || 0)]);
  plBody.push([
    { content: "TOTAL OPERATING EXPENSES", styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
    { content: fmt(totalOpex), styles: { fontStyle: "bold", lineWidth: { top: 0.5 } } },
  ]);
  plBody.push(["", ""]);

  // Net Profit
  plBody.push([
    {
      content: "NET PROFIT",
      styles: { fontStyle: "bold", fillColor: [240, 240, 240], lineWidth: { top: 0.8, bottom: 0.5 } },
    },
    {
      content: fmt(netProfit),
      styles: { fontStyle: "bold", fillColor: [240, 240, 240], lineWidth: { top: 0.8, bottom: 0.5 } },
    },
  ]);
  plBody.push([
    { content: "Net Profit Margin", styles: { fontStyle: "bold" } },
    { content: fmtPct(netMargin), styles: { fontStyle: "bold" } },
  ]);

  autoTable(doc, {
    startY,
    head: [
      [
        { content: "Description", styles: { halign: "left" } },
        { content: "RM", styles: { halign: "right" } },
      ],
    ],
    body: plBody,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 10,
      textColor: [30, 30, 30],
      cellPadding: { top: 3, bottom: 3, left: 4, right: 4 },
      lineColor: [80, 80, 80],
    },
    headStyles: {
      fontStyle: "bold",
      fillColor: [235, 235, 235],
      textColor: [0, 0, 0],
      lineWidth: { top: 0.5, bottom: 0.5 },
    },
    columnStyles: {
      0: { halign: "left", cellWidth: contentWidth - 130 },
      1: { halign: "right", cellWidth: 130 },
    },
    margin: { left: margin, right: margin },
  });

  // Double underline below Net Profit line
  const finalY = doc.lastAutoTable.finalY;
  doc.setLineWidth(0.5);
  doc.line(pageWidth - margin - 130, finalY + 2, pageWidth - margin, finalY + 2);
  doc.line(pageWidth - margin - 130, finalY + 5, pageWidth - margin, finalY + 5);

  // ===== KEY EVENT METRICS =====
  let metricsY = finalY + 24;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("KEY EVENT METRICS", margin, metricsY);
  metricsY += 4;
  doc.setLineWidth(0.4);
  doc.line(margin, metricsY, pageWidth - margin, metricsY);
  metricsY += 6;

  autoTable(doc, {
    startY: metricsY,
    body: [
      ["Total Event Days", String(event.totalEventDays)],
      ["Total Units Sold", (event.totalProductQuantitySold || 0).toLocaleString("en-MY")],
      ["Average Revenue Per Day", fmt(event.averageSalesPerDay || 0)],
      ["Average Net Profit Per Day", fmt(event.averageNetProfitPerDay || 0)],
    ],
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 10,
      textColor: [30, 30, 30],
      cellPadding: { top: 2, bottom: 2, left: 4, right: 4 },
    },
    columnStyles: {
      0: { halign: "left", cellWidth: contentWidth - 130 },
      1: { halign: "right", cellWidth: 130 },
    },
    margin: { left: margin, right: margin },
  });

  // ===== INTERNAL MGMT REPORT LABEL =====
  const disclaimerY = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "italic");
  doc.setFontSize(8);
  const disclaimer =
    "This is an internal event-level management report prepared from the information recorded in the Gao Chill Event P&L Tracker. Supporting invoices, receipts, payment records, and bank records should be retained separately for accounting and tax purposes.";
  const disclaimerLines = doc.splitTextToSize(disclaimer, contentWidth);
  doc.text(disclaimerLines, margin, disclaimerY);

  // ===== SCHEDULE A — REVENUE AND COST OF GOODS SOLD =====
  doc.addPage();

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("SCHEDULE A — REVENUE AND COST OF GOODS SOLD", margin, margin + 30);
  doc.setLineWidth(0.4);
  doc.line(margin, margin + 36, pageWidth - margin, margin + 36);

  let scheduleAY = margin + 48;

  if (categoryTotals.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.text("No products recorded for this event.", margin, scheduleAY);
  } else {
    categoryTotals.forEach((cat, idx) => {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.text(cat.category, margin, scheduleAY);
      scheduleAY += 4;

      const rows = cat.products.map((p) => [
        p.productName,
        (p.quantitySold || 0).toLocaleString("en-MY"),
        p.unit,
        fmt(p.totalSalesAmount || 0),
        fmt(p.unitCost || 0),
        fmt(p.productCost || 0),
        fmt((p.totalSalesAmount || 0) - (p.productCost || 0)),
      ]);

      rows.push([
        {
          content: `Category Totals — ${cat.category}`,
          colSpan: 3,
          styles: { fontStyle: "bold", halign: "left" },
        },
        { content: fmt(cat.revenue), styles: { fontStyle: "bold" } },
        "",
        { content: fmt(cat.cogs), styles: { fontStyle: "bold" } },
        { content: fmt(cat.gross), styles: { fontStyle: "bold" } },
      ]);

      autoTable(doc, {
        startY: scheduleAY,
        head: [["Product", "Qty", "Unit", "Revenue RM", "Unit Cost RM", "COGS RM", "Gross Profit RM"]],
        body: rows,
        theme: "grid",
        styles: {
          font: "helvetica",
          fontSize: 9,
          textColor: [30, 30, 30],
          cellPadding: 3,
          lineColor: [150, 150, 150],
        },
        headStyles: {
          fontStyle: "bold",
          fillColor: [235, 235, 235],
          textColor: [0, 0, 0],
          halign: "center",
        },
        columnStyles: {
          0: { halign: "left" },
          1: { halign: "right" },
          2: { halign: "left" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: { halign: "right" },
        },
        margin: { left: margin, right: margin },
      });

      scheduleAY = doc.lastAutoTable.finalY + 14;
    });

    // Overall totals
    autoTable(doc, {
      startY: scheduleAY,
      body: [
        [
          { content: "Total Revenue", styles: { fontStyle: "bold" } },
          { content: fmt(totalRevenue), styles: { fontStyle: "bold" } },
        ],
        [
          { content: "Total Cost of Goods Sold", styles: { fontStyle: "bold" } },
          { content: fmt(totalCogs), styles: { fontStyle: "bold" } },
        ],
        [
          { content: "Total Gross Profit", styles: { fontStyle: "bold" } },
          { content: fmt(grossProfit), styles: { fontStyle: "bold" } },
        ],
      ],
      theme: "plain",
      styles: {
        font: "helvetica",
        fontSize: 10,
        textColor: [30, 30, 30],
        cellPadding: 3,
        lineColor: [80, 80, 80],
      },
      columnStyles: {
        0: { halign: "left", cellWidth: contentWidth - 130 },
        1: { halign: "right", cellWidth: 130, lineWidth: { top: 0.5 } },
      },
      margin: { left: margin, right: margin },
    });
  }

  // ===== SCHEDULE B — STAFF WAGES =====
  const scheduleBStartY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : margin + 60) + 30;

  // Check if we need a new page
  if (scheduleBStartY > pageHeight - 150) {
    doc.addPage();
  }

  const schedBY = scheduleBStartY > pageHeight - 150 ? margin + 30 : scheduleBStartY;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("SCHEDULE B — STAFF WAGES", margin, schedBY);
  doc.setLineWidth(0.4);
  doc.line(margin, schedBY + 6, pageWidth - margin, schedBY + 6);

  const staff = event.staffWages || [];
  if (staff.length === 0) {
    doc.setFont("helvetica", "italic");
    doc.setFontSize(10);
    doc.text("No staff wages recorded for this event.", margin, schedBY + 22);
  } else {
    const staffRows = staff.map((s) => [s.staffName, fmt(s.wageAmount), s.remark || "—"]);
    staffRows.push([
      { content: "TOTAL STAFF WAGES", styles: { fontStyle: "bold", halign: "left" } },
      { content: fmt(totalStaffWages), styles: { fontStyle: "bold", halign: "right" } },
      "",
    ]);

    autoTable(doc, {
      startY: schedBY + 12,
      head: [["Staff Name", "Wage RM", "Remark"]],
      body: staffRows,
      theme: "grid",
      styles: {
        font: "helvetica",
        fontSize: 9,
        textColor: [30, 30, 30],
        cellPadding: 3,
        lineColor: [150, 150, 150],
      },
      headStyles: {
        fontStyle: "bold",
        fillColor: [235, 235, 235],
        textColor: [0, 0, 0],
      },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right", cellWidth: 100 },
        2: { halign: "left" },
      },
      margin: { left: margin, right: margin },
    });
  }

  // ===== SCHEDULE C — OTHER EVENT EXPENSES AND NOTES =====
  let schedCStartY = staff.length === 0 ? schedBY + 42 : doc.lastAutoTable.finalY + 30;

  if (schedCStartY > pageHeight - 180) {
    doc.addPage();
    schedCStartY = margin + 30;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("SCHEDULE C — OTHER EVENT EXPENSES AND NOTES", margin, schedCStartY);
  doc.setLineWidth(0.4);
  doc.line(margin, schedCStartY + 6, pageWidth - margin, schedCStartY + 6);

  const otherRows = [
    ["Event Rental", fmt(event.rentalCost || 0)],
    ["Transport Expense", fmt(event.transportCost || 0)],
    ["Other Event Expenses", fmt(event.otherCost || 0)],
  ];
  if (event.otherCostRemark && event.otherCostRemark.trim()) {
    otherRows.push([
      { content: "Other Cost Remark", styles: { fontStyle: "bold" } },
      { content: event.otherCostRemark, styles: { halign: "left" } },
    ]);
  }
  if (event.generalNotes && event.generalNotes.trim()) {
    otherRows.push([
      { content: "General Event Notes", styles: { fontStyle: "bold" } },
      { content: event.generalNotes, styles: { halign: "left" } },
    ]);
  }

  autoTable(doc, {
    startY: schedCStartY + 12,
    head: [["Description", "Amount / Detail"]],
    body: otherRows,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      textColor: [30, 30, 30],
      cellPadding: 3,
      lineColor: [150, 150, 150],
    },
    headStyles: {
      fontStyle: "bold",
      fillColor: [235, 235, 235],
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { halign: "left", cellWidth: contentWidth - 200 },
      1: { halign: "right", cellWidth: 200 },
    },
    didParseCell: (data) => {
      // For remark/notes rows, left-align the detail column
      if (data.section === "body" && data.column.index === 1) {
        const raw = data.cell.raw;
        if (typeof raw === "object" && raw?.styles?.halign === "left") {
          data.cell.styles.halign = "left";
        }
      }
    },
    margin: { left: margin, right: margin },
  });

  // ===== SALES RECONCILIATION AND ESTIMATED COGS SCHEDULE =====
  let reconStartY = doc.lastAutoTable.finalY + 30;
  if (reconStartY > pageHeight - 200) {
    doc.addPage();
    reconStartY = margin + 30;
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("SALES RECONCILIATION AND ESTIMATED COGS", margin, reconStartY);
  doc.setLineWidth(0.4);
  doc.line(margin, reconStartY + 6, pageWidth - margin, reconStartY + 6);

  const reconRows = [
    ["Total Sales Collected", fmt(totalRevenue)],
    ["Total Recorded Product Sales", fmt(totalRecordedProductSales)],
    ["Unrecorded Sales Amount", fmt(unrecordedSalesAmount)],
    ["Standard Cup Selling Price", fmt(event.standardCupSellingPrice || 0)],
    ["Raw Unrecorded Cup Equivalent", `${(event.rawUnrecordedCupEquivalent || 0).toFixed(2)} cups`],
    ["Estimated Whole Cups Used for Costing", `${event.estimatedUnrecordedWholeCups || 0} ${(event.estimatedUnrecordedWholeCups || 0) === 1 ? "cup" : "cups"}`],
    ["Conservative Cup Unit Cost", fmt(event.conservativeCupUnitCost || 0)],
    ["Estimated Unrecorded COGS", fmt(estimatedUnrecordedCogs)],
    ["Rounding Method", event.roundingMethod || "Rounded upward to the next whole cup"],
    ["Reconciliation Status", event.reconciliationStatus || "Manual Review Required"],
  ];

  autoTable(doc, {
    startY: reconStartY + 12,
    head: [["Description", "Value"]],
    body: reconRows,
    theme: "grid",
    styles: {
      font: "helvetica",
      fontSize: 9,
      textColor: [30, 30, 30],
      cellPadding: 3,
      lineColor: [150, 150, 150],
    },
    headStyles: {
      fontStyle: "bold",
      fillColor: [235, 235, 235],
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { halign: "left", cellWidth: contentWidth - 200 },
      1: { halign: "right", cellWidth: 200 },
    },
    margin: { left: margin, right: margin },
  });

  // Add explanatory note if estimate was used
  if ((event.estimatedUnrecordedWholeCups || 0) > 0) {
    let noteY = doc.lastAutoTable.finalY + 12;
    if (noteY > pageHeight - 80) {
      doc.addPage();
      noteY = margin + 30;
    }
    doc.setFont("helvetica", "italic");
    doc.setFontSize(8);
    doc.setTextColor(60, 60, 60);
    const cupPrice = (event.standardCupSellingPrice || 0).toFixed(2);
    const cupCost = (event.conservativeCupUnitCost || 0).toFixed(2);
    const note = `Certain standard Cup Herbal Tea sales were not recorded at product level. The exact product mix was unavailable. The related Cost of Goods Sold was estimated using the standard selling price of RM${cupPrice} and the conservative unit cost of RM${cupCost}. The estimated cup equivalent was rounded upward to the next whole cup because partial physical cups cannot be sold.`;
    const noteLines = doc.splitTextToSize(note, contentWidth);
    doc.text(noteLines, margin, noteY);
    doc.setTextColor(0, 0, 0);
  }

  // ===== PAGE HEADER AND FOOTER ON EVERY PAGE =====
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Header (skip on page 1 as it already has the main title)
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    if (i > 1) {
      doc.text("Gao Chill — Event Management Accounts", margin, margin - 15);
      doc.setLineWidth(0.2);
      doc.line(margin, margin - 10, pageWidth - margin, margin - 10);
    }

    // Footer
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.setLineWidth(0.2);
    doc.line(margin, pageHeight - 30, pageWidth - margin, pageHeight - 30);
    doc.text("Internal Use", margin, pageHeight - 18);
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 18, { align: "right" });
    doc.text(event.eventName, pageWidth / 2, pageHeight - 18, { align: "center" });

    doc.setTextColor(0, 0, 0);
  }

  // ===== SAVE THE FILE =====
  const filename =
    "Gao-Chill-Management-P&L-" +
    sanitizeFilename(event.eventName) +
    "-" +
    event.startDate +
    "-to-" +
    event.endDate +
    ".pdf";

  doc.save(filename);
  return filename;
};
